/* eslint-disable no-await-in-loop */
import React, { useEffect, useRef, useState } from "react";

import {
  IStyle,
  mergeStyles,
  MessageBar,
  MessageBarType,
  PrimaryButton
} from "@bps/fluent-ui";

export interface IDownloadWidget {
  buttonText: string;
  buttonStyles?: IStyle;
  isReadOnly?: boolean;
  getSurveyVariable?: (name: string) => any;
}

export const DownloadInfoWidget: React.FC<IDownloadWidget> = ({
  buttonText,
  buttonStyles,
  isReadOnly,
  getSurveyVariable
}) => {
  const [error, setError] = useState<string | undefined>();
  const [infoMessage, setInfoMessage] = useState<string | undefined>();
  const [isDownloading, setIsDownloading] = useState(false);
  const downloadedUrlRef = useRef<string | undefined>();
  const buttonRootStyles: IStyle = mergeStyles({ width: 280 }, buttonStyles);

  useEffect(
    () => () => {
      if (downloadedUrlRef.current?.startsWith("blob:")) {
        URL.revokeObjectURL(downloadedUrlRef.current);
      }
    },
    []
  );

  if (!isReadOnly) {
    return null;
  }

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (isDownloading) {
      return;
    }

    if (downloadedUrlRef.current) {
      window.open(downloadedUrlRef.current, "_blank");
      return;
    }

    setError(undefined);

    const getDocumentDownloadData = getSurveyVariable?.(
      "_getDocumentDownloadData"
    ) as (() => Promise<any>) | undefined;

    if (!getDocumentDownloadData) {
      setError("Download is not available.");
      return;
    }

    setIsDownloading(true);
    setInfoMessage("Preparing your download...");
    try {
      const downloadOptions = await getDocumentDownloadData();

      const pollPeriod = downloadOptions.pollPeriodSeconds;
      const timeOutPeriod = downloadOptions.timeOutPeriodSeconds;
      const blobUrl = downloadOptions.blobUrl;

      // Poll until document is ready or timeout occurs
      const maxIterations = Math.ceil(timeOutPeriod / pollPeriod);
      let lastError: string | null = null;

      for (let i = 0; i < maxIterations; i++) {
        try {
          const response = await fetch(blobUrl);

          // 404 = document not ready yet, continue polling
          if (response.status === 404) {
            await new Promise(resolve =>
              setTimeout(resolve, pollPeriod * 1000)
            );

            continue;
          }

          // Success status = document ready, download blob
          if (response.ok) {
            setInfoMessage("Download is starting...");
            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            downloadedUrlRef.current = objectUrl;
            window.open(objectUrl, "_blank");
            setTimeout(() => setInfoMessage(undefined), 3000);
            return;
          }

          // Other error status
          lastError = `Server returned status ${response.status}`;
          break;
        } catch (fetchError) {
          lastError =
            fetchError instanceof Error
              ? fetchError.message
              : "Network error during polling";
          break;
        }
      }

      // Timeout reached or error occurred
      setInfoMessage(undefined);
      setError(
        lastError ||
          "Document is not ready for download. Please try again later."
      );
    } catch (err) {
      setInfoMessage(undefined);
      setError(
        err instanceof Error ? err.message : "Error downloading document"
      );
    } finally {
      setIsDownloading(false);
    }
  };
  return (
    <>
      <PrimaryButton
        primary
        onClick={handleClick}
        disabled={isDownloading}
        // eslint-disable-next-line react/forbid-component-props
        styles={{
          root: buttonRootStyles,
          rootDisabled: buttonRootStyles
        }}
        iconProps={{ iconName: "Download" }}
      >
        {buttonText}
      </PrimaryButton>
      {infoMessage && (
        <MessageBar
          messageBarType={MessageBarType.info}
          isMultiline={false}
          onDismiss={() => setInfoMessage(undefined)}
        >
          {infoMessage}
        </MessageBar>
      )}
      {error && (
        <MessageBar
          messageBarType={MessageBarType.error}
          isMultiline={false}
          onDismiss={() => setError(undefined)}
        >
          {error}
        </MessageBar>
      )}
    </>
  );
};
